import {HighlightStyle} from '@codemirror/language';
import {tags as t} from '@lezer/highlight';

/**
 * VS Code Dark+ highlight style — the single source of truth for dark mode
 * syntax highlighting across all code editors (code block, code embed,
 * page settings, dashboard custom code, style panel custom settings).
 */
export const darkSyntaxColors = {
  keyword: '#569CD6',
  controlKeyword: '#C586C0',
  variable: '#9CDCFE',
  classType: '#4EC9B0',
  tag: '#569CD6',
  attributeName: '#9CDCFE',
  propertyName: '#9CDCFE',
  function: '#DCDCAA',
  number: '#B5CEA8',
  operator: '#D4D4D4',
  regexp: '#D16969',
  string: '#CE9178',
  comment: '#6A9955',
  constant: '#4FC1FF',
  link: '#569CD6',
  invalid: '#F44747',
  escape: '#D7BA7D',
} as const;

const keywordColor = darkSyntaxColors.keyword;
const controlKeywordColor = darkSyntaxColors.controlKeyword;
const variableColor = darkSyntaxColors.variable;
const classTypeColor = darkSyntaxColors.classType;
const tagColor = darkSyntaxColors.tag;
const attributeNameColor = darkSyntaxColors.attributeName;
const propertyNameColor = darkSyntaxColors.propertyName;
const functionColor = darkSyntaxColors.function;
const numberColor = darkSyntaxColors.number;
const operatorColor = darkSyntaxColors.operator;
const regexpColor = darkSyntaxColors.regexp;
const stringColor = darkSyntaxColors.string;
const commentColor = darkSyntaxColors.comment;
const constantColor = darkSyntaxColors.constant;
const linkColor = darkSyntaxColors.link;
const invalidColor = darkSyntaxColors.invalid;
const escapeColor = darkSyntaxColors.escape;

export const wfCodeDarkHighlightStyle = HighlightStyle.define([
  {
    tag: [
      t.keyword,
      t.operatorKeyword,
      t.modifier,
      t.color,
      t.constant(t.name),
      t.standard(t.name),
      t.special(t.brace),
      t.atom,
      t.bool,
      t.special(t.variableName),
    ],
    color: keywordColor,
  },
  {tag: [t.controlKeyword, t.moduleKeyword], color: controlKeywordColor},
  {
    tag: [
      t.name,
      t.deleted,
      t.character,
      t.macroName,
      t.variableName,
      t.labelName,
      t.definition(t.name),
    ],
    color: variableColor,
  },
  {tag: [t.propertyName], color: propertyNameColor},
  {tag: [t.attributeName], color: attributeNameColor},
  {tag: t.heading, fontWeight: 'bold', color: tagColor},
  {tag: [t.tagName, t.angleBracket, t.standard(t.tagName)], color: tagColor},
  {
    tag: [
      t.typeName,
      t.className,
      t.changed,
      t.annotation,
      t.self,
      t.namespace,
    ],
    color: classTypeColor,
  },
  {
    tag: [t.function(t.variableName), t.function(t.propertyName)],
    color: functionColor,
  },
  {tag: [t.number], color: numberColor},
  {tag: [t.constant(t.variableName)], color: constantColor},
  {
    tag: [t.operator, t.punctuation, t.separator, t.url],
    color: operatorColor,
  },
  {tag: [t.escape], color: escapeColor},
  {tag: [t.regexp], color: regexpColor},
  {
    tag: [
      t.special(t.string),
      t.processingInstruction,
      t.string,
      t.inserted,
      t.attributeValue,
    ],
    color: stringColor,
  },
  {tag: [t.meta, t.comment], color: commentColor},
  {tag: t.link, color: linkColor, textDecoration: 'underline'},
  {tag: t.invalid, color: invalidColor},
  {tag: t.strong, fontWeight: 'bold'},
  {tag: t.emphasis, fontStyle: 'italic'},
  {tag: t.strikethrough, textDecoration: 'line-through'},
]);
